/**
 * MeetingCardContent - Reusable calendar event / meeting display content.
 *
 * Shows meeting title, start/end datetime boxes, attendees (as EntityBadgeByEmail
 * pills that resolve to users or contacts), and optional location.
 */

import { MarkerPin01 } from '@untitledui/icons';

import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import { useOrgTimeFormat } from '@/lib/time';
import { cx } from '@/utils/cx';

import { EntityBadgeByEmail } from '../components/badges/EntityBadgeByEmail';
import { DateTimeBox } from './DateTimeBox';
import { formatDateTimeCompact } from './formatDateTime';

export interface MeetingAttendee {
  /** Display name */
  name: string;
  /** Email address */
  email: string;
}

export interface MeetingCardContentProps {
  /** Meeting title */
  title: string;
  /** ISO start time, or null when not yet resolved */
  startTime: string | null;
  /** ISO end time, or null when not yet resolved */
  endTime: string | null;
  /** Attendees list */
  attendees?: MeetingAttendee[];
  /** Location */
  location?: string;
  /** Organization ID for entity resolution */
  orgId?: number;
  className?: string;
}

export function MeetingCardContent({
  title,
  startTime,
  endTime,
  attendees,
  location,
  orgId,
  className,
}: MeetingCardContentProps) {
  const { timeFormat } = useOrgTimeFormat();
  const timezone = useDisplayTimezone();
  const start = formatDateTimeCompact(startTime, timezone, timeFormat);
  const end = formatDateTimeCompact(endTime, timezone, timeFormat);

  return (
    <div className={cx('space-y-3', className)}>
      {/* Title */}
      <p className='text-sm font-semibold text-fg-primary'>{title}</p>

      {/* Datetime boxes */}
      <div className='flex items-center gap-2'>
        <DateTimeBox label='Start' date={start.date} time={start.time} />
        <span className='text-fg-tertiary text-sm shrink-0'>&ndash;</span>
        <DateTimeBox label='End' date={end.date} time={end.time} />
      </div>

      {/* Attendees */}
      {attendees && attendees.length > 0 && (
        <div>
          <p className='text-xs font-medium text-fg-secondary mb-1.5'>
            Attendees
          </p>
          <div className='flex flex-wrap gap-1.5'>
            {attendees.map(attendee =>
              orgId !== undefined ? (
                <EntityBadgeByEmail
                  key={attendee.email}
                  email={attendee.email}
                  orgId={orgId}
                  variant='default'
                  avatar
                  showEmail
                />
              ) : (
                <span
                  key={attendee.email}
                  className='text-sm text-fg-secondary'
                >
                  {attendee.name || attendee.email}
                </span>
              )
            )}
          </div>
        </div>
      )}

      {/* Location */}
      {location && (
        <div className='flex items-center gap-2.5'>
          <MarkerPin01 className='w-4 h-4 text-fg-tertiary shrink-0' />
          <p className='text-sm text-fg-secondary truncate'>{location}</p>
        </div>
      )}
    </div>
  );
}
